"use client";

import Image from "next/image";
import { useEffect, useMemo, useRef, useState } from "react";

import { usePrefersReducedMotion } from "@/lib/hooks/useMediaQuery";

export type TrustedLogo = {
  slug: string;
  alt: string;
};

type TrustedByMarqueeClientProps = {
  logos: TrustedLogo[];
};

const marqueeSpeedPxPerSecond = 90;
const marqueeRampMs = 240;

export default function TrustedByMarqueeClient({ logos }: TrustedByMarqueeClientProps) {
  const trackRef = useRef<HTMLDivElement>(null);
  const loopRowRef = useRef<HTMLDivElement>(null);
  const loopDistanceRef = useRef(0);
  const reducedMotion = usePrefersReducedMotion();
  const [isHidden, setIsHidden] = useState(false);

  useEffect(() => {
    const handleVisibility = () => {
      setIsHidden(document.hidden);
    };
    document.addEventListener("visibilitychange", handleVisibility);
    return () => document.removeEventListener("visibilitychange", handleVisibility);
  }, []);

  useEffect(() => {
    const track = trackRef.current;
    const loopRow = loopRowRef.current;
    if (!track || !loopRow || reducedMotion) {
      return;
    }

    let rafId = 0;
    let lastTs = 0;
    let travel = 0;
    let currentSpeed = marqueeSpeedPxPerSecond;
    let resizeObserver: ResizeObserver | null = null;

    const measureLoopDistance = () => {
      const rowWidth = loopRow.getBoundingClientRect().width;
      const trackStyles = window.getComputedStyle(track);
      const trackGap = Number.parseFloat(trackStyles.columnGap || trackStyles.gap || "0") || 0;
      loopDistanceRef.current = rowWidth + trackGap;
    };

    const step = (ts: number) => {
      if (!lastTs) {
        lastTs = ts;
      }
      const delta = ts - lastTs;
      lastTs = ts;

      const targetSpeed = isHidden ? 0 : marqueeSpeedPxPerSecond;
      const ramp = Math.min(delta / marqueeRampMs, 1);
      currentSpeed += (targetSpeed - currentSpeed) * ramp;
      travel += (currentSpeed * delta) / 1000;

      const loopDistance = loopDistanceRef.current;
      if (loopDistance > 0) {
        travel %= loopDistance;
        track.style.transform = `translate3d(${-travel}px, 0, 0)`;
      }

      rafId = window.requestAnimationFrame(step);
    };

    measureLoopDistance();
    if (typeof ResizeObserver !== "undefined") {
      resizeObserver = new ResizeObserver(measureLoopDistance);
      resizeObserver.observe(track);
      resizeObserver.observe(loopRow);
    } else {
      window.addEventListener("resize", measureLoopDistance);
    }

    rafId = window.requestAnimationFrame(step);
    return () => {
      window.cancelAnimationFrame(rafId);
      resizeObserver?.disconnect();
      window.removeEventListener("resize", measureLoopDistance);
    };
  }, [isHidden, reducedMotion]);

  useEffect(() => {
    if (reducedMotion && trackRef.current) {
      trackRef.current.style.transform = "translate3d(0, 0, 0)";
    }
  }, [reducedMotion]);

  const marqueeRows = useMemo(() => {
    return reducedMotion ? [logos] : [logos, logos];
  }, [logos, reducedMotion]);

  return (
    <section id="marcas" className="bg-[color:var(--color-white)] pb-[clamp(48px,6.5vw,120px)]">
      <div className="relative mt-[clamp(10px,3vw,44px)] overflow-hidden">
        <div
          className="pointer-events-none absolute inset-y-0 left-0 w-[clamp(32px,6vw,110px)] bg-[linear-gradient(90deg,rgba(255,255,255,1)_0%,rgba(255,255,255,0)_100%)]"
          aria-hidden
        />
        <div
          className="pointer-events-none absolute inset-y-0 right-0 w-[clamp(32px,6vw,110px)] bg-[linear-gradient(270deg,rgba(255,255,255,1)_0%,rgba(255,255,255,0)_100%)]"
          aria-hidden
        />
        <div
          ref={trackRef}
          className="flex items-center gap-[clamp(12px,1.6vw,30px)] will-change-transform"
        >
          {marqueeRows.map((row, rowIndex) => (
            <div
              key={`marquee-row-${rowIndex}`}
              ref={rowIndex === 0 ? loopRowRef : undefined}
              data-marquee-row
              className="flex items-end gap-[clamp(12px,1.6vw,30px)]"
            >
              {row.map((logo, index) => {
                return (
                  <div
                    key={`${logo.slug}-${rowIndex}-${index}`}
                    className="flex h-[clamp(88px,9.2vw,172px)] w-[clamp(86px,9.8vw,186px)] items-center justify-center rounded-[12px] bg-[color:var(--color-red)] px-[clamp(14px,1.8vw,32px)]"
                  >
                    <Image
                      src={`/images/trusted-by/${logo.slug}.svg`}
                      alt={logo.alt}
                      width={120}
                      height={40}
                      className="h-auto w-full max-w-[120px]"
                    />
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
